import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, NAV_NETWORK, PULSE_TABS } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";
import { List, X, Plus, CaretDown, SignOut, User, House, ArrowUpRight, MagnifyingGlass } from "@phosphor-icons/react";
import LanguageSelector from "@/components/LanguageSelector";
import LocationPill from "@/components/LocationPill";

/**
 * Single grouped dropdown used for both the primary tabs (Explore, Observe,
 * etc.) and the sister-site "SocialPulse Network ▾" chip.
 */
function GroupedDropdown({ trigger, items, testId, dropdownAlign = "left" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      <AnimatePresence>
        {open && (
          <motion.div
            key="dd"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-full ${dropdownAlign === "right" ? "right-0" : "left-0"} mt-1 w-72 rounded-2xl bg-white border border-black/10 shadow-2xl overflow-hidden z-50`}
            role="menu"
            data-testid={testId}
          >
            {items.map((it) => {
              const isExternal = /^https?:\/\//.test(it.to);
              const Comp = isExternal ? "a" : Link;
              const props = isExternal ? { href: it.to, target: "_blank", rel: "noreferrer" } : { to: it.to };
              return (
                <Comp
                  key={it.label}
                  {...props}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-dd-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex flex-col px-4 py-2.5 hover:bg-[#F7F3E8] border-b border-black/5 last:border-b-0 transition-colors"
                  role="menuitem"
                >
                  <span className="font-mono-tech text-[10.5px] tracking-[0.22em] uppercase text-[#241209] flex items-center gap-1.5">
                    {it.label}{isExternal && <ArrowUpRight size={9} weight="bold" className="opacity-70" />}
                  </span>
                  {it.description && (
                    <span className="mt-0.5 text-[11.5px] normal-case tracking-normal text-[#6B5A44] leading-snug">
                      {it.description}
                    </span>
                  )}
                </Comp>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Close avatar dropdown on outside click / Escape
  useEffect(() => {
    if (!userMenu) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setUserMenu(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenu]);

  return (
    <header data-testid="site-nav" className="fixed top-0 left-0 right-0 z-50">
      {/* Thin top strip: geographic context. Kept out of the main nav row so
          it can never push primary tab links off-screen. */}
      <div className="bg-white/85 backdrop-blur-xl border-b border-black/8">
        <div className="max-w-[1600px] mx-auto flex items-center justify-end gap-3 px-6 md:px-10 py-1.5">
          <LocationPill compact />
          <div className="hidden md:block"><LanguageSelector variant="light" /></div>
        </div>
      </div>
      <div className="bg-white/90 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4 px-6 md:px-10 py-3">
          <Link to="/" className="flex flex-col items-start leading-tight group shrink-0" data-testid="brand-link">
            <span className="font-serif-display text-xl md:text-[22px] tracking-tight text-[#14532D]">
              NaturePulse<span className="text-[10px] align-super text-[#B85F2A] ml-0.5">™</span>
            </span>
            <span className="hidden md:inline font-mono-tech text-[9px] tracking-[0.28em] text-[#6B5A44]/80 uppercase -mt-0.5">
              A Social Pulse Endeavor
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 min-w-0 flex-wrap justify-end" data-testid="primary-nav">
            {NAV_LINKS.map((group) => {
              const groupActive = group.items?.some((it) => !/^https?:/.test(it.to) && location.pathname === it.to.replace(/#.*$/, "")) || location.pathname === group.to;
              return (
                <GroupedDropdown
                  key={group.label}
                  testId={`nav-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
                  items={group.items}
                  trigger={({ open, toggle }) => (
                    <button
                      type="button"
                      onClick={toggle}
                      data-testid={`nav-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-expanded={open}
                      className={`inline-flex items-center gap-1 px-2.5 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase whitespace-nowrap transition-colors ${
                        groupActive || open ? "text-[#B85F2A]" : "text-[#4A3B2C] hover:text-[#14532D]"
                      }`}
                    >
                      {group.label}
                      <CaretDown size={9} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                  )}
                />
              );
            })}
            <GroupedDropdown
              testId="nav-group-network"
              items={NAV_NETWORK}
              dropdownAlign="right"
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  data-testid="nav-network"
                  aria-expanded={open}
                  className={`inline-flex items-center gap-1 ml-1 rounded-full border font-mono-tech text-[9.5px] tracking-[0.22em] uppercase whitespace-nowrap px-2.5 py-1 transition-colors ${
                    open
                      ? "bg-[#B85F2A] text-white border-[#B85F2A]"
                      : "border-[#B85F2A]/40 bg-[#B85F2A]/8 text-[#B85F2A] hover:bg-[#B85F2A] hover:text-white"
                  }`}
                >
                  SocialPulse Network
                  <CaretDown size={9} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
              )}
            />
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/search"
              data-testid="nav-search"
              aria-label="Search the archive"
              className="inline-flex items-center justify-center rounded-full border border-black/25 text-[#241209] w-8 h-8 hover:bg-[#241209] hover:text-white transition-colors"
            >
              <MagnifyingGlass size={13} weight="bold" />
            </Link>
            <Link
              to="/#identify"
              data-testid="nav-add-sighting"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-black/25 text-[#241209] px-3.5 py-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#241209] hover:text-white transition-colors"
            >
              <Plus size={12} weight="bold" /> Add Sighting
            </Link>
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenu((v) => !v)}
                  data-testid="nav-user-menu-toggle"
                  aria-expanded={userMenu}
                  aria-label="Account menu"
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/20 hover:border-[#B85F2A] px-1 py-1 pr-2 transition-colors"
                >
                  <img
                    src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=E4A15C&color=0A1620`}
                    alt={user.name || "You"}
                    className="w-7 h-7 rounded-full"
                  />
                  <CaretDown
                    size={10}
                    weight="bold"
                    className={`text-[#6B5A44] transition-transform ${userMenu ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence>
                  {userMenu && (
                    <motion.div
                      key="user-menu"
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-black/10 shadow-2xl overflow-hidden"
                      role="menu"
                      data-testid="nav-user-menu"
                    >
                      <div className="px-4 py-3 border-b border-black/8">
                        <div className="text-[13px] font-serif-display text-[#241209] truncate">{user.name || "You"}</div>
                        {user.email && <div className="mt-0.5 font-mono-tech text-[9.5px] tracking-[0.2em] uppercase text-[#6B5A44]/80 truncate">{user.email}</div>}
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenu(false)}
                        data-testid="user-menu-dashboard"
                        className="flex items-center gap-2.5 px-4 py-2.5 font-mono-tech text-[10.5px] tracking-[0.24em] uppercase text-[#4A3B2C] hover:bg-[#F7F3E8] hover:text-[#241209] transition-colors"
                        role="menuitem"
                      >
                        <House size={13} weight="bold" /> Dashboard
                      </Link>
                      <Link
                        to="/publisher"
                        onClick={() => setUserMenu(false)}
                        data-testid="user-menu-publisher"
                        className="flex items-center gap-2.5 px-4 py-2.5 font-mono-tech text-[10.5px] tracking-[0.24em] uppercase text-[#4A3B2C] hover:bg-[#F7F3E8] hover:text-[#241209] transition-colors"
                        role="menuitem"
                      >
                        <User size={13} weight="bold" /> Publisher
                      </Link>
                      <div className="border-t border-black/8">
                        <button
                          type="button"
                          onClick={() => { setUserMenu(false); logout(); }}
                          data-testid="logout-btn"
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 font-mono-tech text-[10.5px] tracking-[0.24em] uppercase text-[#4A3B2C] hover:bg-[#F7F3E8] hover:text-[#241209] transition-colors"
                          role="menuitem"
                        >
                          <SignOut size={13} weight="bold" /> Sign out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <LoginButton label="Log in" className="border-black/25 text-[#241209] hover:bg-[#241209] hover:text-white" />
            )}
            <button
              className="lg:hidden text-[#241209] p-2"
              onClick={() => setOpen(v => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} weight="thin" /> : <List size={22} weight="thin" />}
            </button>
          </div>
        </div>
      </div>

      {/* Pulse docket tabs — direct, always-visible links to each section.
          Light pastel pills with dark text (readable on the light chrome). */}
      <div className="bg-[#FBF9F1]/95 backdrop-blur-xl border-b border-black/8">
        <div className="max-w-[1600px] mx-auto flex items-center gap-1.5 px-6 md:px-10 py-1.5 overflow-x-auto" data-testid="pulse-tabs" role="navigation" aria-label="Pulse sections">
          {PULSE_TABS.map((t) => {
            const active = !t.external && (location.pathname === t.to || location.pathname.startsWith(`${t.to}/`));
            const style = active
              ? { backgroundColor: `${t.color}26`, borderColor: t.color, color: "#241209" }
              : undefined;
            const cls = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono-tech text-[9.5px] tracking-[0.18em] uppercase whitespace-nowrap transition-colors " +
              (active ? "" : "border-black/15 text-[#4A3B2C] hover:text-[#241209] hover:border-black/35");
            const dotColor = active ? t.color : t.color;
            const dot = <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />;
            return t.external ? (
              <a key={t.label} href={t.to} data-testid={t.testid} className={cls} style={style}>
                {dot}{t.label} <span className="text-[8px]">↗</span>
              </a>
            ) : (
              <Link key={t.label} to={t.to} data-testid={t.testid} className={cls} style={style} aria-current={active ? "page" : undefined}>
                {dot}{t.label}
              </Link>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white/97 backdrop-blur-xl border-b border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A] mb-1.5">Pulse Sections</div>
                <div className="flex flex-col gap-0.5 pl-2 border-l border-black/10">
                  {PULSE_TABS.map((t) =>
                    t.external ? (
                      <a key={t.label} href={t.to} onClick={() => setOpen(false)} data-testid={`mobile-${t.testid}`}
                        className="py-1.5 font-serif-display text-lg text-[#4A3B2C]">
                        {t.label} <span className="text-xs align-middle">↗</span>
                      </a>
                    ) : (
                      <Link key={t.label} to={t.to} onClick={() => setOpen(false)} data-testid={`mobile-${t.testid}`}
                        className={`py-1.5 font-serif-display text-lg ${location.pathname.startsWith(t.to) ? "text-[#B85F2A]" : "text-[#241209]"}`}>
                        {t.label}
                      </Link>
                    )
                  )}
                </div>
              </div>
              {NAV_LINKS.map((group) => (
                <div key={group.label}>
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A] mb-1.5">{group.label}</div>
                  <div className="flex flex-col gap-0.5 pl-2 border-l border-black/10">
                    {group.items.map((it) => (
                      <Link
                        key={it.label}
                        to={it.to}
                        onClick={() => setOpen(false)}
                        className={`py-1.5 font-serif-display text-lg ${location.pathname === it.to ? "text-[#B85F2A]" : "text-[#241209]"}`}
                      >{it.label}</Link>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A] mb-1.5">SocialPulse Network</div>
                <div className="flex flex-col gap-0.5 pl-2 border-l border-black/10">
                  {NAV_NETWORK.map((l) => (
                    <a key={l.label} href={l.to} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="py-1.5 font-serif-display text-lg text-[#4A3B2C]">
                      {l.label} <span className="text-xs align-middle">↗</span>
                    </a>
                  ))}
                </div>
              </div>
              {user && (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 font-serif-display text-xl text-[#241209]">Dashboard</Link>
              )}
              <Link to="/civic-reports#add" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center gap-2 rounded-full border border-black/25 text-[#241209] px-4 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase w-fit">
                <Plus size={12} weight="bold" /> Add Sighting
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
